import * as React from 'react';
import { observer } from 'mobx-react';
import { PageGiveViewModel } from './page-give-view-model';
import { WebGivingUserAction } from '../../webgiving-user-actions';
import { Form } from '../../primitives/form';
import { SubmitButton } from '../../components/submit-button/submit-button';
import { FormControlAmount } from '../../components/amount';
import { FormControl } from '../../components/form-control/form-control';
import { FormControlSplitButton } from '../../components/split-button';
import { DatePickerField } from '../../components/form-fields/date-picker-field';
import { SelectField } from '../../components/form-fields';
import { FormControlNumber } from '../../components/number-input';
import { Formatter } from '../../../Shared/utils/formatter';
import { ReferenceFields } from '../../components/reference-fields/reference-fields';
import { EndType, FrequencyCode, PaymentLabel } from '../../webgiving-generated';
import { ErrorMessage } from '../../components/error-message/error-message';
import { FormField } from '../../components/form-field';
import errorSvg from '../../assets/svg/error.svg';
import { SvgIcon } from '../../components/svg-icon';
import { FadeTransition, fadeTransitionDuration } from '../../components/fade-transition';
import { FocusOnMount } from '../../components/focus-on-mount-hoc';
import { getPageClasses } from '../helpers/get-page-classes';
import { getSizeName } from '../../components/amount-display/amount-display';
import { classNames } from '../../../Shared/utils/classnames';
import { injectWebGivingProps, InjectableWebGivingProps, raiseAction } from '../../utils/injectable-props';
import * as style from './page-give.less';
import { pageFooter } from '../../main/webgiving-main.less';
import { PanelFooter } from '../../components/panel-footer';
import { OptionalDate } from '../../components/date-picker/date-picker';
import { Text } from '../../components/text';

export { PageGiveViewModel };

@injectWebGivingProps
@observer
export class PageGive extends React.Component<{
	vm: PageGiveViewModel,
	transitionState: string,
} & InjectableWebGivingProps> {
	panelBodyNode: HTMLDivElement;
	contentNode: HTMLDivElement;

	componentDidMount() {
		// Fetch App Content Card when this page loads
		raiseAction(new WebGivingUserAction.FetchAppContentCard(), this.props);
	}

	render() {
		const { transitionState, vm } = this.props;
		const {
			amount,
			giftType,
			giftTypeOptions,
			isRecurring,
			referenceFieldsViewModel,
			error,
			paymentLabel: { VerbSentenceCase, NounSentenceCase },
			minEndDate,
			isLoading,
			isBlocked,
			pageShouldBeVisible,
			brandingPackage,
			merchantInfo,
			recurringSelectorIsVisible,
			fundVisibility,
			showsOnlyAmount,
			disableValidation,
		} = vm;
		const {
			OneTimeFutureGiftEnabled: oneTimeFutureGiftEnabled,
		} = merchantInfo;
		const elementsShouldEnter = transitionState === 'entered';
		const hasCustomGivingTitle = !!(brandingPackage && brandingPackage.GivingTitle);
		const hasEnhancedTradingName = !!(brandingPackage && brandingPackage.EnhancedLogoImageUrl);

		const headerClasses = classNames(
			style.header,
			{
				[style.headerHasEnhancedTradingName]: hasEnhancedTradingName,
			},
		);
		const givingTitleClasses = classNames(
			style.heading,
			{
				[style.givingTitle]: hasCustomGivingTitle,
			},
		);

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading)} data-pp-at-target={pageShouldBeVisible ? `Give page` : null}>
				<Form onSubmit={this.handleSubmit}>
					<div className={this.getStyleBasedOnShow(showsOnlyAmount, 'panel-body panel-body-form')} ref={this.savePanelBodyNode}>
						<FadeTransition in={elementsShouldEnter} onExit={this.setContentHeight}>
							<div>
								<header className={headerClasses}>
									{hasEnhancedTradingName &&
										<h2 className={style.enhancedTradingName} data-pp-at-target="Merchant enhanced trading name">{merchantInfo.TradingName}</h2>
									}
									<h1 className={ givingTitleClasses } data-pp-at-target="Giving title">
										{/* If not for typescript misinterpreting types, this would be:
											{hasCustomGivingTitle ? brandingPackage.GivingTitle : VerbSentenceCase}
										*/}
										{ (brandingPackage && brandingPackage.GivingTitle) || (<Text>{ VerbSentenceCase }</Text>) }
									</h1>
									<AmountInput amount={amount} acceptanceTestTargetId="Amount input" disableValidation={disableValidation} />
								</header>
								<div className={this.getStyleBasedOnShow(showsOnlyAmount, 'panel-body-content')} ref={this.saveContentNode}>
									<ErrorMessage error={error} />
									{recurringSelectorIsVisible &&
										<FormControl label={`${NounSentenceCase} type`}>
											<FormControlSplitButton
												field={giftType}
												options={giftTypeOptions}
												uniformWidth={true}
												ariaLabel={`${NounSentenceCase} type`}
												acceptanceTestTargetId="Gift type"
											/>
										</FormControl>}
									{isRecurring &&
										<RecurringPaymentFields
											{...this.props.vm}
											minEndDate={minEndDate}
											payerSpecifiedRecurringEndDateEnabled={vm.merchantInfo.PayerSpecifiedRecurringEndDateEnabled} />}
									<ReferenceFields vm={referenceFieldsViewModel}
													fundVisibility={fundVisibility} />
									{!isRecurring && oneTimeFutureGiftEnabled &&
										<OneTimePaymentFields
											{...this.props.vm}
											minEndDate={minEndDate}
										/>
									}
								</div>
							</div>
						</FadeTransition>
					</div>
					<PanelFooter className={pageFooter}>
						<SubmitButton acceptanceTestTargetId="Next" isLoading={isLoading} isBlocked={isBlocked}><Text>Next</Text></SubmitButton>
					</PanelFooter>
				</Form>
			</div>
		);
	}

	getStyleBasedOnShow = (showsOnlyAmount: boolean, styleWhenShow: string) => showsOnlyAmount ? '' : styleWhenShow;

	setContentHeight = () => this.panelBodyNode.style.minHeight = `${this.contentNode.offsetHeight}px`;

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private saveContentNode = (contentNode: HTMLDivElement) => this.contentNode = contentNode;

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		raiseAction(new WebGivingUserAction.SubmitCurrentPage(), this.props);
	}
}

export interface IOneTimePaymentFieldsProps {
	oneTimeGiftDate: FormField<OptionalDate>;
	minStartDate: Date;
	minEndDate: Date;
	maxDate: Date;
}

@observer
export class OneTimePaymentFields extends React.Component<IOneTimePaymentFieldsProps, any> {
	render() {
		const { oneTimeGiftDate, minStartDate, maxDate } = this.props;
		return (
			<div className="row">
				<FormControl label="When" validationError={oneTimeGiftDate.validationError} className={'col-sm-6 ' + style.noRightPaddingOnDesktop}>
					<DatePickerField
						formField={oneTimeGiftDate}
						options={{ minDate: minStartDate, maxDate: maxDate }}
						acceptanceTestTargetId="One time gift date" />
				</FormControl>
			</div>
		);
	}
}

export interface IRecurringPaymentFieldsProps {
	payerSpecifiedRecurringEndDateEnabled: boolean;
	frequency: FormField<FrequencyCode>;
	frequencyOptions: { value: FrequencyCode, label: string }[];
	startDate: FormField<OptionalDate>;
	endType: FormField<EndType>;
	endTypeOptions: { value: EndType, label: string }[];
	endDate: FormField<OptionalDate>;
	endOccurrences: FormField<number | null>;
	minStartDate: Date;
	minEndDate: Date;
	maxDate: Date;
	paymentLabel: PaymentLabel;
	handleFrequencyChange: (x: any) => void;
	handleStartDateChange: (d: OptionalDate) => void;
}

@observer
export class RecurringPaymentFields extends React.Component<IRecurringPaymentFieldsProps, any> {

	render() {
		const { frequency, frequencyOptions, startDate, minStartDate, maxDate, payerSpecifiedRecurringEndDateEnabled, handleFrequencyChange, handleStartDateChange } = this.props;
		return (
			<div>
				<FormControl label="Frequency" validationError={frequency.validationError}>
					<FormControlSplitButton
						field={frequency}
						options={frequencyOptions}
						uniformWidth={true}
						ariaLabel="Frequency"
						acceptanceTestTargetId="Gift Frequency"
						onChange={handleFrequencyChange}
					/>
				</FormControl>
				<div className="row">
					<FormControl label="Starting" validationError={startDate.validationError} className={'col-sm-6 ' + style.noRightPaddingOnDesktop}>
						<DatePickerField
							formField={startDate}
							options={{ minDate: minStartDate, maxDate: maxDate, disableDayFn: this.disableDays }}
							acceptanceTestTargetId="Recurring Start Date"
							onChange={handleStartDateChange} />
					</FormControl>
				</div>
				{payerSpecifiedRecurringEndDateEnabled && <RecurringEndInputs {...this.props} />}
			</div>
		);
	}

	disableDays: (date: Date) => boolean = (date) => {
		if (this.props.frequency.value === FrequencyCode.FirstAndFifteenth) {
			return date.getDate() !== 1 && date.getDate() !== 15;
		}
		return false;
	}
}

@observer
export class RecurringEndInputs extends React.Component<IRecurringPaymentFieldsProps, any> {
	render() {
		const { endType, endTypeOptions, endDate, endOccurrences, minEndDate, maxDate, paymentLabel } = this.props;
		const { NounPluralLowerCase } = paymentLabel;
		let containerClass, endingClass, detailsInput;
		switch (endType.value) {
			case EndType.Occurrences:
				containerClass = style.recurringEnd;
				endingClass = style.recurringEndType;
				detailsInput = (
					<FormControl label={`Number of ${NounPluralLowerCase}`} className={style.recurringEndDetails} validationError={endOccurrences.validationError}>
						<FormControlNumber
							formField={endOccurrences}
							maxLength={4}
							min={1}
							acceptanceTestTargetId="Ending Number of Occurrences"
							placeholder="e.g. 1-1000"
						/>
					</FormControl>
				);
				break;
			case EndType.Date:
				containerClass = style.recurringEnd;
				endingClass = style.recurringEndType;
				detailsInput = (
					<FormControl label="End date" className={style.recurringEndDetails} validationError={endDate.validationError}>
						<DatePickerField
							formField={endDate}
							options={{ minDate: minEndDate, maxDate: maxDate }}
							acceptanceTestTargetId="Recurring End Date"
							placeholder="Select end date"
						/>
					</FormControl>
				);
				break;
			default:
				containerClass = 'row';
				endingClass = 'col-sm-6 ' + style.noRightPaddingOnDesktop;
				detailsInput = null;
				break;
		}

		return (
			<div className={containerClass}>
				<FormControl label="Ending" className={endingClass}>
					<SelectField formField={endType} options={endTypeOptions} acceptanceTestTargetId="Recurring End Type" />
				</FormControl>
				{detailsInput}
			</div>

		);
	}
}

@observer
class AmountInput extends React.Component<{
	amount: FormField<number>;
	acceptanceTestTargetId: string;
	disableValidation?: boolean;
}> {
	render() {
		const { amount, acceptanceTestTargetId, disableValidation } = this.props;
		const formattedAmount = Formatter.formatNumberForDisplay(amount.value, 2);
		const wrapperClassName = `${style.wrapper} ${amount.value === 0 ? style.zero : ''}`;
		const errorClass = classNames({
			[style.amountValidationError]: amount.hasValidationError,
		});

		return (
			<div className="form-control-container">
				<div className={`${style.amount} ${getSizeName(formattedAmount)}`}>
					<FocusOnMount delay={fadeTransitionDuration}>
						<FormControlAmount value={amount.value}
							dataFieldInvalid={amount.hasValidationError}
							onChange={amount.updateValue}
							onBlur={!disableValidation ? amount.validateEveryTimeCauseImASpecialCase : undefined}
							className={style.input}
							acceptanceTestTargetId={acceptanceTestTargetId}
							ariaDescribedBy="amount-validation-error"
							ariaInvalid={amount.hasValidationError}
							ariaRequired={true}
							amountIsLocked={amount.isLocked}
						/>
					</FocusOnMount>
					<div className={style.decorator}>
						<span className={wrapperClassName}>
							<span className={classNames(style.dollar)} aria-hidden="true" role="presentation">$</span>
							<span className={style.underline}>{formattedAmount}</span>
						</span>
					</div>
				</div>
				<div id="amount-validation-error" className={errorClass} role="alert" aria-atomic="true">
					{amount.hasValidationError && <SvgIcon svg={errorSvg} className={style.errorIcon} ariaLabel="There is a problem with the amount field:" />}
					{amount.hasValidationError && <div className={style.errorContent}>{!!amount.validationError ? <Text params={amount.getTranslationParams()}>{amount.validationError}</Text> : amount.validationError}</div>}
				</div>
			</div>
		);
	}
}
